import { FC } from 'lib/component-utils'
import { GetServerSideProps } from 'next'
import { useUserContext } from 'lib/context'
import { withUserProps } from 'lib/contextCreation'
import { useImmer } from 'use-immer'
import AuthLayout from 'components/auth/AuthLayout'
import UserInfoForm from 'components/auth/UserInfoForm'
import { SignUpProcessState } from 'components/auth/lib/types'
import ConfirmationCodeForm from 'components/auth/ConfirmationCodeForm'
import { useIntl } from 'lib/intl-utils'
import { useEffect, useState } from 'react'
import { PromoCode } from 'lib/dto'
import nookies from 'nookies'
import { PromoCodeContext } from 'components/landing/lib/promoCodeContext'

export const getServerSideProps: GetServerSideProps = withUserProps(
  async ({ pageContext, userContext }) => {
    if (!['sellmonitor', 'sellscreen', 'uzum'].includes(userContext.theme.hostType)) {
      return {
        notFound: true,
      }
    }

    if (userContext.isAuthorized && process.env.NODE_ENV === 'production') {
      return {
        redirect: {
          permanent: false,
          destination: '/products/list/',
        },
      }
    }

    const redirect = pageContext.query.redirect ?? null
    const error = pageContext.query.error ?? null

    return {
      props: {
        isResponsive: true,
        disableFooter: true,
        error,
        redirect,
      },
    }
  },
  { forceUnauthorizedMode: process.env.NODE_ENV === 'development', allowUnauthorizedUsers: true }
)

type Props = {
  error?: string
  redirect?: string
}

const SignupPage: FC<Props> = ({ error, redirect }) => {
  const ctx = useUserContext()
  const { t, lang } = useIntl()
  const [promoCode, setPromoCode] = useState<PromoCode | null>(null)
  const [defaultTrialDays, setDefaultTrialDays] = useState<number | null>(null)

  useEffect(() => {
    const cookies = nookies.get()
    const code = cookies['promoCode']
    if (code) {
      ctx.getPromoCodeSignup(code).then((data) => {
        if (!Array.isArray(data)) setPromoCode(data)
      })
    }
  }, [])

  useEffect(() => {
    ctx.getDefaultTrialDaysSignup().then(setDefaultTrialDays)
  }, [])

  const [processState, setProcessState] = useImmer<SignUpProcessState>({
    type: 'signup',
    stage: 'enteringData',
    data: null,
    codeType: 'sms',
    phone: '',
  })

  return (
    <PromoCodeContext.Provider value={promoCode ?? null}>
      <AuthLayout pageName={t`registration.sign_up`}>
        {processState.stage === 'enteringData' && (
          <UserInfoForm
            processState={processState}
            setProcessState={setProcessState}
            defaultTrialDays={defaultTrialDays}
            error={error}
            redirect={redirect}
          />
        )}
        {processState.stage === 'waitingForCode' && (
          <ConfirmationCodeForm
            processState={processState}
            setProcessState={setProcessState}
            redirect={redirect}
          />
        )}
      </AuthLayout>
    </PromoCodeContext.Provider>
  )
}

export default SignupPage
